import {
    DateFormats,
    OfficeManagerActions,
    OfficeManagerActivities,
    TOffices,
} from "intake_shared/src/types";
import * as React from "react";
import { AclContext } from "../../../../contexts/AclContext";
import store from "../../../../store";
import { Dropdown } from "../../bits/Dropdown/Dropdown";
import { Button } from "../../bits/Button/Button";
import FilterButton from "../../bits/FilterButton/FilterButton";
import RefreshIcon from "../../icons/Refresh";
import { TableHeader } from "../../../../types";
import moment from "moment";
import { OFFICE_MANAGER_ACTIVITIES_COLORS } from "../../../../constants";
import {
    addSpaceToString,
    sortStringsWithNumberEndings,
} from "../../../../utils/utils";
import TablePagination from "../../bits/Table/TablePagination";
import { uniq } from "lodash";
import Table from "../../bits/Table/Table";
import EmptyListState from "../../bits/EmptyListState/EmptyListState";
import { Badge } from "../../bits/Badge/Badge";
import ShortenedText from "../../bits/ShortenedText/ShortenedText";

interface OfficeManagerActivitiesProps {
    officeManagerLocations: string[] | undefined;
    filteredOffices: TOffices;
}

export default function OfficeManagerActivitiesList({
    officeManagerLocations,
    filteredOffices,
}: OfficeManagerActivitiesProps) {
    const [officeWindowRequestsActivities, setOfficeWindowRequestsActivities] =
        React.useState<OfficeManagerActivities[]>();

    const loggedInUserEmail = React.useContext(AclContext)?.authUser.user?.email;
    const [refreshKey, setRefreshKey] = React.useState(0);
    const [selectedActivity, setSelectedActivity] = React.useState("");
    const [selectedLocation, setSelectedLocation] = React.useState("");
    const [selectedOfficeNumber, setSelectedOfficeNumber] = React.useState("");
    const [currentPage, setCurrentPage] = React.useState(1);
    const pageLimit = 50;
    const [nextButtonDisabled, setNextButtonDisabled] = React.useState(false);

    const getOfficeManagerActivities = async () => {
        const filters = {
            location: selectedLocation,
            office: selectedOfficeNumber,
            activity: selectedActivity,
        };
        const requests = await store.retrieveOfficeManagerActivities(
            loggedInUserEmail as string,
            currentPage,
            pageLimit,
            filters
        );
        setOfficeWindowRequestsActivities(requests);
        // Check if there is more data for the next page
        setNextButtonDisabled(requests.length < pageLimit);
    };

    React.useEffect(() => {
        getOfficeManagerActivities();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        refreshKey,
        currentPage,
        selectedActivity,
        selectedLocation,
        selectedOfficeNumber,
    ]);

    const columns = React.useMemo(() => {
        const headers: TableHeader[] = [
            {
                Header: "Office Manager",
                id: "office_manager",
                accessor: (row: OfficeManagerActivities) => (
                    <p className="caseLdClName">{row.office_manager || ""}</p>
                ),
            },
            {
                Header: "DATE",
                id: "timestamp",
                accessor: (row: OfficeManagerActivities) => (
                    <p className="caseLdClName">{`${moment(row.timestamp).format(
                        DateFormats.DAY_YEAR_HOUR_MIN_FORMAT
                    )}`}</p>
                ),
            },
            {
                Header: "REQUETSED DAY",
                id: "",
                accessor: (row: OfficeManagerActivities) => (
                    <p className="caseLdClName">{`${moment(row.start).format(
                        DateFormats.DAY
                    )}`}</p>
                ),
            },
            {
                Header: "REQUETSED BY",
                id: "",
                accessor: (row: OfficeManagerActivities) => (
                    <p className="caseLdClName">{row.requested_by || "--"}</p>
                ),
            },
            {
                Header: "REQUESTED TIME",
                id: "start_date",
                accessor: (row: OfficeManagerActivities) => (
                    <p className="caseLdClName">{`${moment(row.start).format(
                        DateFormats.HOUR_MIN_FORMAT
                    )} - ${moment(row.end).format(DateFormats.HOUR_MIN_FORMAT)}`}</p>
                ),
            },
            {
                Header: "LOCATION",
                id: "location",
                accessor: (row: OfficeManagerActivities) => (
                    <p>{row.location || ""}</p>
                ),
            },
            {
                Header: "OFFICE NO",
                id: "office_number",
                accessor: (row: OfficeManagerActivities) => (
                    <p>{row.office || ""}</p>
                ),
            },
            {
                Header: "ACTION",
                id: "action",
                accessor: (row: OfficeManagerActivities) => (
                    <Badge
                        bgColor={
                            OFFICE_MANAGER_ACTIVITIES_COLORS[row.activity || ""]
                        }
                        content={addSpaceToString(row.activity as string) || ""}
                        textColor="var(--color-white)"
                    />
                ),
            },
            {
                Header: "ASSIGNED TO",
                id: "assigned_to",
                accessor: (row: OfficeManagerActivities) => (
                    <p>{row?.assigned_to ? row?.assigned_to : "--"}</p>
                ),
            },
            {
                Header: "COMMENTS",
                id: "comments",
                accessor: (row: OfficeManagerActivities) => (
                    <p>
                        {row.reason || row.approved_with_changes ? (
                            <ShortenedText
                                notes={row.reason || row.approved_with_changes}
                                identifyingKey={row.activity}
                                maxLength={10}
                            />
                        ) : (
                            "--"
                        )}
                    </p>
                ),
            },
        ];
        return headers;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleResetFilters = () => {
        setSelectedLocation("");
        setSelectedActivity("");
        setSelectedOfficeNumber("");
        setCurrentPage(1);
    };

    const handleFirstPageClick = () => {
        setCurrentPage(1);
    };

    const handlePrevClick = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    const handleNextClick = () => {
        setCurrentPage(currentPage + 1);
    };

    const tableFooter = (
        <TablePagination
            currentPage={currentPage}
            onFirstPage={handleFirstPageClick}
            onPrevPage={handlePrevClick}
            onNextPage={handleNextClick}
            isNextDisabled={nextButtonDisabled}
            showPrevButton={currentPage > 1}
        />
    );

    const getLocationLabel = (location: string) => {
        return location || "Select location";
    };

    const getOfficeLabel = (office: string) => {
        return office || "Select office";
    };

    const getActivityLabel = (activity: string) => {
        return activity ? addSpaceToString(activity) : "Select activity";
    };

    return (
        <div>
            <div className="office-manager-filters">
                <Dropdown>
                    <Dropdown.Trigger>
                        <FilterButton text={getLocationLabel(selectedLocation)} />
                    </Dropdown.Trigger>

                    <Dropdown.Content width="170px">
                        <Dropdown.Item
                            closeMenuOnSelect
                            isSelected={selectedLocation === ""}
                            onClick={() => setSelectedLocation("")}
                        >
                            Show all
                        </Dropdown.Item>
                        {officeManagerLocations?.map((locations) => (
                            <Dropdown.Item
                                key={locations}
                                closeMenuOnSelect
                                isSelected={selectedLocation === locations}
                                onClick={() => setSelectedLocation(locations)}
                            >
                                {locations}
                            </Dropdown.Item>
                        ))}
                    </Dropdown.Content>
                </Dropdown>
                <Dropdown>
                    <Dropdown.Trigger>
                        <FilterButton text={getOfficeLabel(selectedOfficeNumber)} />
                    </Dropdown.Trigger>
                    <Dropdown.Content>
                        <Dropdown.Item
                            closeMenuOnSelect
                            isSelected={selectedOfficeNumber === ""}
                            onClick={() => setSelectedOfficeNumber("")}
                        >
                            Show all
                        </Dropdown.Item>
                        {sortStringsWithNumberEndings(
                            uniq(filteredOffices[selectedLocation] || [])
                        ).map((r) => (
                            <Dropdown.Item
                                key={r}
                                onClick={() => setSelectedOfficeNumber(r)}
                                isSelected={selectedOfficeNumber === r}
                            >
                                {r}
                            </Dropdown.Item>
                        ))}
                    </Dropdown.Content>
                </Dropdown>
                <Dropdown>
                    <Dropdown.Trigger>
                        <FilterButton text={getActivityLabel(selectedActivity)} />
                    </Dropdown.Trigger>
                    <Dropdown.Content>
                        <Dropdown.Item
                            closeMenuOnSelect
                            isSelected={selectedActivity === ""}
                            onClick={() => setSelectedActivity("")}
                        >
                            Show all
                        </Dropdown.Item>
                        {Object.values(OfficeManagerActions).map((activity) => (
                            <Dropdown.Item
                                key={activity}
                                onClick={() => setSelectedActivity(activity)}
                                isSelected={selectedActivity === activity}
                            >
                                {addSpaceToString(activity as string) || ""}
                            </Dropdown.Item>
                        ))}
                    </Dropdown.Content>
                </Dropdown>
                <Button onClick={() => setRefreshKey((prevKey) => prevKey + 1)}>
                    <RefreshIcon />
                </Button>
                <Button
                    variant="normal"
                    className="reset-filters-button"
                    onClick={handleResetFilters}
                >
                    Reset Filters
                </Button>
            </div>
            {officeWindowRequestsActivities &&
                (officeWindowRequestsActivities.length === 0 ? (
                    <EmptyListState
                        emptyMessage="No office window requests activities"
                        stateHelperText="Office window requests activities will be shown here"
                    />
                ) : (
                    <Table
                        columns={columns}
                        data={officeWindowRequestsActivities}
                        allowSelect={false}
                        footer={tableFooter}
                    />
                ))}
        </div>
    );
}
